import { captureException } from '@sentry/react'
import { notice } from 'app/notice'
import LookySvg from 'assets/social/looky.svg'
import TelegramSvg from 'assets/social/telegram.svg'
import WhatsappSvg from 'assets/social/whatsapp.svg'
import { Button } from 'component/Button'
import { Qr } from 'component/Qr'
import { Divider, Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { TelegramShareButton, WhatsappShareButton } from 'react-share'
import { api } from 'store/api'
import { controller } from 'store/controller'
import { InferenceStore } from 'store/inference/InferenceStore'
import { me } from 'store/me'
import { opt } from 'store/opt'
import { store } from 'store/store'
import { buildShareLink, buildShareText, buildViewLink } from 'type/Inference'
import { shareMobile } from 'util/share'
import { shareTelegram } from 'util/telegram'
import css from './ShareContent.module.scss'

type Props = {
  inference: InferenceStore
  fullscreen?: boolean
}

function qr() {
  return !store.tg
}

function social() {
  return !opt.stand && !opt.boutique && !me.external
}

const Buttons = observer(({ inference }: Props) => {
  const { tg, mobile } = store
  const { url } = inference
  const link = buildShareLink(inference.inference_id, inference.links)
  const text = buildShareText(inference.product, inference.brand, inference.gender)
  const initData = store.tgWebAppData
  const atelier = opt.atelier
  const gap = 8
  const cn = css.button

  const onShareTelegram = useLatestCallback(async () => {
    controller.onShare('bot', inference)
    store.carousel.share = false
    const inference_id = inference.inference_id
    try {
      if (initData) {
        await api.shareInference(initData, inference_id)
        notice.success(i18n('SharedSuccessfully'))
        return
      }
    } catch (e) {
      console.error(e)
      const product = opt.options.product
      captureException(e, { extra: { initData, product, inference_id } })
    }
    shareTelegram(link, text)
    notice.success(i18n('SharedSuccessfully'))
  })

  const onShareMobile = useLatestCallback(async () => {
    controller.onShare('mobile', inference)
    store.carousel.share = false
    const origin = store.meta?.location
    if (url && await shareMobile(url, link, origin)) notice.success(i18n('SharedSuccessfully'))
  })

  const onShareLooky = useLatestCallback(() => {
    controller.onShare('looky', inference)
    store.carousel.share = false
    if (mobile) {
      window.open(store.getLookyShareLink(url))
      notice.success(i18n('SharedSuccessfully'))
    } else {
      runInAction(() => {
        store.carousel.looky = true
        store.carousel.share = false
      })
    }
  })

  const onClickTelegram = useLatestCallback(() => {
    controller.onShare('telegram', inference)
    store.carousel.share = false
    notice.success(i18n('SharedSuccessfully'))
  })
  const onClickWhatsapp = useLatestCallback(() => {
    controller.onShare('whatsapp', inference)
    store.carousel.share = false
    notice.success(i18n('SharedSuccessfully'))
  })

  const buttons = []

  if (tg) {
    if (opt.share.has('telegram')) {
      buttons.push(<Button key={'tg'} className={cn} large link action={onShareTelegram}>
        <TelegramSvg />
        <Typo>{initData ? i18n('ShareInTelegramBot') : i18n('ShareInTelegram')}</Typo>
      </Button>)
    }
    if (!atelier && opt.share.has('looky')) {
      buttons.push(<Button key={'looky'} large link className={cn} action={onShareLooky}>
        <LookySvg />
        <Typo>{i18n('Looky')}</Typo>
      </Button>)
    }
  } else if (mobile) {
    buttons.push(<Button key={'share'} className={cn} large link action={onShareMobile}>
      <Icon name="ios_share" size={20} />
      <Typo>{i18n('Share')}</Typo>
    </Button>)
    if (!atelier && opt.share.has('looky')) {
      buttons.push(<Button key={'looky'} className={cn} large link action={onShareLooky}>
        <LookySvg />
        <Typo>{i18n('Looky')}</Typo>
      </Button>)
    }
  } else {
    if (!atelier && opt.share.has('looky')) {
      buttons.push(<Button key={'looky'} className={cn} large link action={onShareLooky}>
        <LookySvg />
        <Typo size={13}>{i18n('Looky')}</Typo>
      </Button>)
    }
    if (opt.share.has('telegram')) {
      buttons.push(<TelegramShareButton key={'tg'} className={cn} onClick={onClickTelegram} url={link} title={text}>
        <TelegramSvg />
        <Typo size={13}>{i18n('Telegram')}</Typo>
      </TelegramShareButton>)
    }
    if (opt.share.has('whatsapp')) {
      buttons.push(<WhatsappShareButton key={'wa'} className={cn} onClick={onClickWhatsapp} url={link} title={text}>
        <WhatsappSvg />
        <Typo size={13}>{i18n('Whatsapp')}</Typo>
      </WhatsappShareButton>)
    }
  }

  if (!buttons.length) return null

  return <Vertical gap={16}>
    {qr() && social() && <Divider />}
    <Space gap={gap} center>
      {buttons}
    </Space>
  </Vertical>
})

export const ShareContent = observer(({ inference, fullscreen }: Props) => {
  const { inference_id } = inference
  const link = buildViewLink(inference_id)
  const description = social()
    ? opt.f4 ? i18n('PointYourCameraOrChooseSocialNetworkToShareTheResultOrBuyItem')
      : i18n('PointYourCameraOrChooseSocialNetworkToShareTheResult')
    : opt.f4 ? i18n('PointYourCameraToShareTheResultOrBuyItem')
      : i18n('PointYourCameraToShareTheResult')

  return <Vertical gap={16}>
    {qr() && <Space center>
      <Qr link={link} size={fullscreen ? 400 : 320} pad={fullscreen ? 40 : 16} />
    </Space>}
    {qr() && <Typo size={13} pre center>
      {description}
    </Typo>}
    {social() && <Buttons inference={inference} />}
  </Vertical>
})
